// @ts-nocheck

import React, { useRef, useCallback, useLayoutEffect, useEffect } from 'react';
import type { ElementRef } from 'react';
import styled, { css } from 'styled-components';

import { ReactComponent as ArrowLeft } from '../../../../assets/svg/chevron-left.svg';
import { ReactComponent as ArrowRight } from '../../../../assets/svg/chevron-right.svg';
import { disableTextSelectionWhileDragging } from '../../../../utils/event';

const Wrap = styled.div`
  position: relative;
  height: 2em;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
`;

const Line = styled.div`
  position: relative;
  width: 100%;
  height: 2px;
  background-color: ${({ $isDisabled, theme }) =>
    $isDisabled ? theme.grids.colors.lightgrey : theme.grids.colors.redlight};
`;

const CursorWrap = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: calc(100% - 2em);
  height: 2em;
`;

const Cursor = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 2em;
  height: 2em;
  box-shadow: 0 2px 4px 0 rgba(48, 52, 58, 0.2);
  background-color: ${({ $isDisabled, theme }) =>
    $isDisabled ? theme.grids.colors.lightgrey : theme.grids.colors.redlight};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
`;

const commonArrow = css`
  width: 30%;
  margin: 0 5%;
  color: #fff;
`;

const PictoLeft = styled(ArrowLeft)`
  ${commonArrow}
`;
const PictoRight = styled(ArrowRight)`
  ${commonArrow}
`;

type GaugeProps = {
  className?: string;
  min: number;
  max: number;
  value: number;
  onChange: (value: number) => void;
  isDisabled?: boolean;
};

const mapFromRange = (value, inMin, inMax, outMin, outMax) => {
  const newValue = ((value - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;
  return Math.max(Math.min(newValue, outMax), outMin);
};

const Gauge = ({ className, min, max, value, onChange, isDisabled = false }: GaugeProps) => {
  const ref = useRef<ElementRef<'div'> | null>(null);
  const refWrapCursor = useRef<ElementRef<'div'> | null>(null);
  const isDown = useRef<boolean>(false);
  const percentPos = useRef<number>(mapFromRange(value, min, max, 0, 100));

  const changeValue = useCallback(() => {
    let newValue = 1;
    if (percentPos.current !== 0) {
      newValue = mapFromRange(percentPos.current, 0, 100, min + 1, max);
    }
    onChange(Math.floor(newValue));
  }, [max, min, onChange]);

  const updateDomPosition = useCallback(() => {
    if (refWrapCursor.current) {
      refWrapCursor.current.style.transform = `translate3d(${percentPos.current}%, 0, 0)`;
    }
  }, []);

  const computePositionFromMouse = useCallback(
    (clientX: number) => {
      if (ref.current && !isDisabled) {
        const { left: startGauge, width: widthGauge } = ref.current.getBoundingClientRect();
        const relativeX = clientX - startGauge;
        percentPos.current = (relativeX / widthGauge) * 100;
        percentPos.current = Math.min(percentPos.current, 100);
        percentPos.current = Math.max(percentPos.current, 0);
        updateDomPosition();
        changeValue();
      }
    },
    [changeValue, isDisabled, updateDomPosition],
  );

  const computePositionFromValue = useCallback(() => {
    if (ref.current && !isDisabled) {
      percentPos.current = mapFromRange(value, min, max, 0, 100);
      updateDomPosition();
    }
  }, [isDisabled, max, min, updateDomPosition, value]);

  const onTouchMove = useCallback(
    (e: SyntheticTouchEvent<HTMLElement>) => {
      if (isDown.current) {
        const clientX = e.touches && e.touches[0]?.clientX;
        computePositionFromMouse(clientX);
      }
    },
    [computePositionFromMouse],
  );

  const onMouseMove = useCallback(
    (e: SyntheticMouseEvent<HTMLElement>) => {
      if (isDown.current) {
        const clientX = e.clientX;
        computePositionFromMouse(clientX);
      }
    },
    [computePositionFromMouse],
  );

  const onClick = useCallback(
    (e: SyntheticMouseEvent<HTMLElement>) => {
      const clientX = e.clientX;
      computePositionFromMouse(clientX);
    },
    [computePositionFromMouse],
  );

  const onDown = useCallback((e) => {
    isDown.current = true;
    return disableTextSelectionWhileDragging(e);
  }, []);

  const onUp = useCallback(() => {
    if (isDown.current) {
      isDown.current = false;
      changeValue();
    }
  }, [changeValue]);

  useEffect(() => {
    computePositionFromValue();
  }, [computePositionFromValue, value]);

  useLayoutEffect(() => {
    window.addEventListener('touchmove', onTouchMove, false);
    window.addEventListener('touchend', onUp, false);
    window.addEventListener('mousemove', onMouseMove, false);
    window.addEventListener('mouseup', onUp, false);
    updateDomPosition();

    return () => {
      window.removeEventListener('touchmove', onTouchMove, false);
      window.removeEventListener('touchend', onUp, false);
      window.removeEventListener('mousemove', onMouseMove, false);
      window.removeEventListener('mouseup', onUp, false);
    };
  }, [onMouseMove, onTouchMove, onUp, updateDomPosition]);

  return (
    <Wrap ref={ref} className={className} onMouseDown={onDown} onTouchStart={onDown} onClick={onClick}>
      <Line $isDisabled={isDisabled} />
      <CursorWrap ref={refWrapCursor}>
        <Cursor $isDisabled={isDisabled}>
          <PictoLeft />
          <PictoRight />
        </Cursor>
      </CursorWrap>
    </Wrap>
  );
};

export default Gauge;
